import { useEffect, useRef, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { useDMSocket } from "@/hooks/useDMSocket";
import { labelTalentType, labelCreatorType } from "@/lib/roleCategories";
import { Send, ArrowLeft, MessageCircle, Loader2, Users, Wifi, WifiOff, ImagePlus, Paperclip } from "lucide-react";
import { toast } from "sonner";
import DMAttachments, { PendingAttachments } from "@/components/DMAttachments";

const typeLabel = (u) => (u.role === "talent" ? labelTalentType(u.type) : labelCreatorType(u.type)) || "";
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "");
const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return fmtTime(iso);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

export default function Messages() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const lastRef = useRef(null);

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf");
    if (!list.length || !tid) return;
    const room = 6 - pending.length;
    if (room <= 0) return toast.error("6 pièces jointes maximum par message");
    const batch = list.slice(0, room);
    setUploading((n) => n + batch.length);
    await Promise.all(batch.map(async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/dm/threads/${tid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPending((p) => (p.length >= 6 ? p : [...p, data]));
      } catch (e) { toast.error(e.response?.data?.detail || `Échec de l'envoi de ${f.name}`); }
      finally { setUploading((n) => n - 1); }
    }));
  };

  const loadThreads = () => api.get("/dm/threads").then(({ data }) => setThreads(Array.isArray(data) ? data : [])).catch(() => {}).finally(() => setLoading(false));

  const loadMessages = async (id, since) => {
    try {
      const { data } = await api.get(`/dm/threads/${id}/messages`, { params: since ? { since } : {} });
      setThread(data.thread);
      setMessages((prev) => {
        const incoming = data.messages || [];
        if (!since) return incoming;
        const known = new Set(prev.map((m) => m.id));
        return [...prev, ...incoming.filter((m) => !known.has(m.id))];
      });
      if ((data.messages || []).length) lastRef.current = data.messages[data.messages.length - 1].created_at;
      if (!since || data.messages?.length) { await api.post(`/dm/threads/${id}/read`).catch(() => {}); window.dispatchEvent(new Event("dm:refresh")); }
    } catch (e) {
      if (e.response?.status === 404) { toast.error("Conversation introuvable"); navigate("/messages"); }
    }
  };

  const { live } = useDMSocket((ev) => {
    if (ev.type === "message") {
      if (ev.thread_id === tid) {
        setMessages((prev) => (prev.some((m) => m.id === ev.message.id) ? prev : [...prev, ev.message]));
        lastRef.current = ev.message.created_at;
        if (ev.message.sender_id !== user?.user_id) api.post(`/dm/threads/${tid}/read`).catch(() => {});
      }
      loadThreads();
    }
    if (ev.type === "connection_accepted") loadThreads();
  });

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (!tid) { setThread(null); setMessages([]); return undefined; }
    lastRef.current = null;
    loadMessages(tid);
    const id = setInterval(() => { if (!live) loadMessages(tid, lastRef.current); }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid, live]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if ((!body && pending.length === 0) || !tid || uploading > 0) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/threads/${tid}/messages`, { body, attachments: pending });
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      lastRef.current = data.created_at;
      setDraft("");
      setPending([]);
      loadThreads();
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); } finally { setSending(false); }
  };
  useEffect(() => { setPending([]); }, [tid]);

  const other = thread?.user;

  return (
    <AppShell>
      <div data-testid="messages-page" className="-mx-2 sm:mx-0">
        <div className="hidden sm:flex items-end justify-between mb-6">
          <div>
            <p className="caption-label text-[#C68E59]">Messagerie privée</p>
            <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Mes <span className="italic text-[#A26F3E]">conversations</span></h1>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-[11px] ${live ? "text-[#2E7D32]" : "text-[#8B8680]"}`} data-testid="dm-live-indicator">{live ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}{live ? "Temps réel" : "Synchronisation"}</span>
        </div>

        <div className="bg-white rounded-3xl border border-[#EADDC9] overflow-hidden grid lg:grid-cols-12 h-[calc(100dvh-170px)] sm:h-[70vh] min-h-[480px]">
          {/* Threads */}
          <aside className={`lg:col-span-4 border-r border-[#FAF3EA] flex-col ${tid ? "hidden lg:flex" : "flex"}`} data-testid="dm-threads-panel">
            <div className="px-5 py-4 border-b border-[#FAF3EA] flex items-center justify-between">
              <p className="caption-label text-[#A26F3E]">Conversations</p>
              <Link to="/reseau" className="text-xs text-[#1A1A1A] hover:text-[#C68E59] inline-flex items-center gap-1" data-testid="dm-go-network"><Users className="w-3.5 h-3.5" /> Réseau</Link>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" /></div>
                : threads.length === 0 ? (
                  <div className="p-8 text-center" data-testid="dm-empty">
                    <MessageCircle className="w-9 h-9 text-[#C68E59] mx-auto opacity-60" />
                    <p className="font-serif text-lg text-[#1A1A1A] mt-3">Aucune conversation</p>
                    <p className="text-xs text-[#8B8680] mt-1">Connectez-vous à un membre depuis votre réseau pour commencer à échanger.</p>
                    <Link to="/reseau" className="inline-flex mt-4 rounded-full bg-[#1A1A1A] text-white text-xs font-semibold px-5 h-9 items-center hover:bg-[#C68E59] transition-colors">Voir les suggestions</Link>
                  </div>
                ) : threads.map((t) => (
                  <Link key={t.id} to={`/messages/${t.id}`} className={`flex items-center gap-3 px-5 py-3.5 hover:bg-[#FFFBF5] transition-colors ${t.id === tid ? "bg-[#FAF3EA]" : ""}`} data-testid={`dm-thread-${t.id}`}>
                    <Avatar u={t.user} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className={`text-sm truncate ${t.unread ? "font-semibold text-[#1A1A1A]" : "text-[#1A1A1A]"}`}>{t.user.name}</p>
                        <span className="text-[10px] text-[#8B8680] shrink-0">{fmtDay(t.last_message_at)}</span>
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${t.unread ? "text-[#1A1A1A]" : "text-[#8B8680]"}`}>{t.last_sender_id === user?.user_id ? "Vous : " : ""}{t.last_message || "Nouvelle connexion — dites bonjour"}</p>
                    </div>
                    {t.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#C68E59] text-white text-[10px] font-bold flex items-center justify-center" data-testid={`dm-unread-${t.id}`}>{t.unread}</span>}
                  </Link>
                ))}
            </div>
          </aside>

          {/* Conversation */}
          <section className={`lg:col-span-8 flex-col ${tid ? "flex" : "hidden lg:flex"}`} data-testid="dm-conversation">
            {!tid ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
                <MessageCircle className="w-10 h-10 text-[#EADDC9]" />
                <p className="font-serif text-xl text-[#1A1A1A] mt-4">Sélectionnez une conversation</p>
              </div>
            ) : (
              <>
                <header className="px-4 sm:px-6 py-3.5 border-b border-[#FAF3EA] flex items-center gap-3">
                  <Link to="/messages" className="lg:hidden w-9 h-9 rounded-full hover:bg-[#FAF3EA] flex items-center justify-center" data-testid="dm-back"><ArrowLeft className="w-4 h-4" /></Link>
                  {other && (
                    <>
                      <Avatar u={other} size="w-10 h-10" />
                      <div className="min-w-0 flex-1">
                        <Link to={other.profile_path} className="font-medium text-[#1A1A1A] hover:underline truncate block" data-testid="dm-other-name">{other.name}</Link>
                        <p className="text-[11px] text-[#8B8680] truncate">{other.online ? <span className="text-[#2E7D32]">En ligne</span> : typeLabel(other)}{other.location ? ` · ${other.location}` : ""}</p>
                      </div>
                    </>
                  )}
                </header>
                <div
                  className={`flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-3 bg-[#FFFBF5] relative ${dragging ? "ring-2 ring-inset ring-[#C68E59]" : ""}`}
                  data-testid="dm-messages"
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                >
                  {dragging && <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#FFFBF5]/80 text-sm text-[#A26F3E] pointer-events-none"><ImagePlus className="w-5 h-5 mr-2" /> Déposez vos photos ou moodboards</div>}
                  {messages.length === 0 && <p className="text-center text-xs text-[#8B8680] py-10">Vous êtes connectés. Lancez la conversation — texte, photos ou moodboard.</p>}
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    const hasAtt = (m.attachments || []).length > 0;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.id}`}>
                        <div className={`max-w-[85%] sm:max-w-[65%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "bg-[#1A1A1A] text-white rounded-br-md" : "bg-white border border-[#EADDC9] text-[#1A1A1A] rounded-bl-md"}`}>
                          {hasAtt && <DMAttachments attachments={m.attachments} mine={mine} />}
                          {m.body && <p className={`whitespace-pre-wrap break-words ${hasAtt ? "mt-2" : ""}`}>{m.body}</p>}
                          <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-[#8B8680]"}`}>{fmtTime(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="p-3 sm:p-4 border-t border-[#FAF3EA] bg-white" onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); addFiles(e.clipboardData.files); } }}>
                  <PendingAttachments items={pending} uploading={uploading} onRemove={(id) => setPending((p) => p.filter((a) => a.file_id !== id))} />
                  <div className="flex items-end gap-2">
                    <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="dm-file-input" />
                    <button type="button" onClick={() => fileRef.current?.click()} className="w-11 h-11 rounded-full border border-[#EADDC9] text-[#6E4B2A] hover:border-[#C68E59] hover:text-[#C68E59] flex items-center justify-center transition-colors shrink-0" title="Ajouter des photos ou un moodboard" data-testid="dm-attach-button">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <textarea
                      value={draft} onChange={(e) => setDraft(e.target.value)} rows={1} maxLength={2000} placeholder={pending.length ? "Ajouter un mot (optionnel)…" : "Écrire un message…"}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
                      className="flex-1 resize-none rounded-2xl border border-[#EADDC9] bg-[#FFFBF5] px-4 py-3 text-sm focus:outline-none focus:border-[#C68E59] max-h-32"
                      data-testid="dm-input"
                    />
                    <button type="submit" disabled={sending || uploading > 0 || (!draft.trim() && pending.length === 0)} className="w-11 h-11 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white flex items-center justify-center transition-colors disabled:opacity-50 shrink-0" data-testid="dm-send">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function Avatar({ u, size = "w-12 h-12" }) {
  return (
    <div className={`${size} rounded-2xl overflow-hidden bg-[#FAF3EA] shrink-0 relative`}>
      <div className="w-full h-full flex items-center justify-center font-serif text-lg text-[#A26F3E]">{(u?.name || "?")[0]}</div>
      {u?.picture && <img src={optimizeUrl(u.picture, 320)} alt={u.name} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      {u?.online && <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-[#2E7D32] border-2 border-white" />}
    </div>
  );
}
